<template>
  <div class="coupon-pool-manage-container">
    <!-- (1) PageHeader 标题 + 右侧主动作 -->
    <ds-page-header
      :title="$t('couponPoolManage.page.title')"
      :subtitle="$t('couponPoolManage.page.subtitle')">
      <template #extra>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">
          {{ $t('couponPoolManage.list.create') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel 筛选区 -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.couponName" :placeholder="$t('couponPoolManage.search.couponName')" clearable
            @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model="searchForm.validityDay" :placeholder="$t('couponPoolManage.search.validityDay')" clearable
            @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density"
        :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('couponPoolManage.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border
        :empty-description="$t('couponPoolManage.page.empty')" @retry="getList">
        <el-table-column prop="poolId" :label="$t('couponPoolManage.table.poolId')" align="center" width="160" />
        <el-table-column prop="couponTypeName" :label="$t('couponPoolManage.table.couponType')" align="center" width="120" />
        <el-table-column prop="couponName" :label="$t('couponPoolManage.table.couponName')" align="left" min-width="160" />
        <el-table-column :label="$t('couponPoolManage.table.actualPrice')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.actualPrice) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('couponPoolManage.table.buyPrice')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatMoney(scope.row.buyPrice) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('couponPoolManage.table.couponStock')" align="right" width="100">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatNumber(scope.row.couponStock, 0) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('couponPoolManage.table.validityDay')" align="right" width="110">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatNumber(scope.row.validityDay, 0) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="seq" :label="$t('couponPoolManage.table.seq')" align="right" width="90" />
        <el-table-column :label="$t('couponPoolManage.table.operation')" align="center" width="150" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" icon="el-icon-edit" @click="handleEdit(scope.row)">
              {{ $t('couponPoolManage.table.edit') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <add-coupon-pool ref="addCouponPool" @success="handleSuccess" />
    <edit-coupon-pool ref="editCouponPool" @success="handleSuccess" />
    <delete-coupon-pool ref="deleteCouponPool" @success="handleSuccess" />
  </div>
</template>


<script>
import { listCouponPool } from '@/api/account/couponPoolManageApi'
import AddCouponPool from '@/components/account/AddCouponPool'
import EditCouponPool from '@/components/account/EditCouponPool'
import DeleteCouponPool from '@/components/account/DeleteCouponPool'
import { formatNumber, formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'CouponPoolManageList',
  components: {
    AddCouponPool,
    EditCouponPool,
    DeleteCouponPool,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        couponName: '',
        validityDay: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.searchForm
        }
        const res = await listCouponPool(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况，
        // 原实现直接取 data 会在此处抛 TypeError，导致筛选后表格仍停留在上一次的数据。
        this.tableData = Array.isArray(res.data) ? res.data : []
        this.page.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('couponPoolManage.page.loadFailed')
        console.error('Failed to load coupon pool list:', error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        couponName: '',
        validityDay: ''
      }
      this.handleSearch()
    },
    handleAdd() {
      this.$refs.addCouponPool.open()
    },
    handleEdit(row) {
      this.$refs.editCouponPool.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteCouponPool.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    },
    formatNumber(value, decimals = 2) {
      return formatNumber(value, decimals)
    },
    formatMoney(value) {
      return formatMoney(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.coupon-pool-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>